import type { AbiEvent } from 'abitype'
import {
  http,
  BaseError,
  type Chain,
  ContractFunctionExecutionError,
  type GetLogsReturnType,
  HttpRequestError,
  InternalRpcError,
  InvalidInputRpcError,
  InvalidParamsRpcError,
  InvalidRequestRpcError,
  JsonRpcVersionUnsupportedError,
  LimitExceededRpcError,
  MethodNotFoundRpcError,
  type MulticallParameters,
  type MulticallReturnType,
  ParseRpcError,
  type PublicClient,
  type ReadContractParameters,
  ResourceNotFoundRpcError,
  ResourceUnavailableRpcError,
  RpcRequestError,
  TimeoutError,
  TransactionNotFoundError,
  TransactionReceiptNotFoundError,
  createPublicClient,
} from 'viem'
import { z } from 'zod'
import type { HttpRpc } from '../chain'
import { waitForRequest } from '../requestScheduler'
import type { LowerHex } from '../types'
import { bigIntMin } from '../utils'
import type { Client } from './types'

/**
 * HTTP 200 でも JSON-RPC の result/error がない応答を検出する。
 * @param params RPC の応答と送信先。本文は複製して viem にも保持する。
 * @returns 有効な応答なら完了し、不正な応答は別 endpoint への切替対象にする。
 */
const validateRpcResponse = async ({
  response,
  url,
}: { response: Response; url: string }): Promise<void> => {
  if (!response.ok) return
  const data = z.record(z.unknown()).safeParse(
    await response
      .clone()
      .json()
      .catch(() => undefined),
  )
  if (
    !data.success ||
    (!Object.hasOwn(data.data, 'result') && !Object.hasOwn(data.data, 'error'))
  ) {
    throw new HttpRequestError({
      url,
      status: 502,
      details: 'Invalid JSON-RPC response: result and error are missing',
    })
  }
}

type EndpointLease = {
  readonly index: number
  readonly endpoint: string
}

/**
 * not-found 発生後の bounded retry で除外済み endpoint と最初の error を保持する。
 */
type NotFoundRetryState = {
  readonly error: TransactionNotFoundError | TransactionReceiptNotFoundError
  readonly excludedEndpointIndexes: Set<number>
}

class Semaphore {
  #endpoints: readonly string[]
  #usingIndexSet = new Set<number>()
  #queue: Array<{
    excludedEndpointIndexes: ReadonlySet<number>
    resolve: (lease: EndpointLease) => void
    reject: (error: Error) => void
    timeoutId: ReturnType<typeof setTimeout>
  }> = []

  constructor(endpoints: readonly string[]) {
    this.#endpoints = endpoints
  }

  acquire(
    timeout: number,
    excludedEndpointIndexes?: ReadonlySet<number>,
  ): Promise<EndpointLease> {
    const endpointIndex = this.#findAvailableEndpointIndex(
      excludedEndpointIndexes,
    )
    if (endpointIndex !== undefined) {
      this.#usingIndexSet.add(endpointIndex)
      return Promise.resolve(this.#createLease(endpointIndex))
    }

    return new Promise<EndpointLease>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const index = this.#queue.findIndex(
          (item) => item.timeoutId === timeoutId,
        )
        if (index !== -1) {
          this.#queue.splice(index, 1)
          reject(new Error('Timeout while waiting for semaphore'))
        }
      }, timeout)

      this.#queue.push({
        excludedEndpointIndexes: new Set(excludedEndpointIndexes),
        resolve,
        reject,
        timeoutId,
      })
    })
  }

  release(lease: EndpointLease, cooldown?: number): void {
    if (cooldown !== undefined) {
      setTimeout(() => {
        this.#releaseNow(lease)
      }, cooldown)
      return
    }
    this.#releaseNow(lease)
  }

  #releaseNow(lease: EndpointLease): void {
    this.#usingIndexSet.delete(lease.index)
    this.#dispatchQueue()
  }

  #dispatchQueue(): void {
    let dispatched = true
    while (dispatched) {
      dispatched = false
      for (const endpointIndex of this.#getAvailableEndpointIndexes()) {
        const queueIndex = this.#queue.findIndex(
          (item) => !item.excludedEndpointIndexes.has(endpointIndex),
        )
        if (queueIndex === -1) {
          continue
        }

        const item = this.#queue[queueIndex]
        if (item === undefined) {
          continue
        }
        this.#queue.splice(queueIndex, 1)
        clearTimeout(item.timeoutId)
        this.#usingIndexSet.add(endpointIndex)
        item.resolve(this.#createLease(endpointIndex))
        dispatched = true
      }
    }
  }

  #findAvailableEndpointIndex(
    excludedEndpointIndexes?: ReadonlySet<number>,
  ): number | undefined {
    for (const [index] of this.#endpoints.entries()) {
      if (
        !this.#usingIndexSet.has(index) &&
        excludedEndpointIndexes?.has(index) !== true
      ) {
        return index
      }
    }
    return undefined
  }

  #getAvailableEndpointIndexes(): number[] {
    const indexes: number[] = []
    for (const [index] of this.#endpoints.entries()) {
      if (!this.#usingIndexSet.has(index)) {
        indexes.push(index)
      }
    }
    return indexes
  }

  #createLease(index: number): EndpointLease {
    const endpoint = this.#endpoints[index]
    if (endpoint === undefined) {
      throw new Error(`Endpoint index is out of range: ${index}`)
    }
    return {
      index,
      endpoint,
    }
  }
}

type CreateClientParameters = {
  chain: Chain
  httpRpcs: HttpRpc[]
}

type PromiseWithTimeoutParameters<T> = {
  readonly promise: Promise<T>
  readonly timeout: number
}

/**
 * Promise に timeout を設定し、resolve/reject 後に timer を確実に破棄する。
 *
 * @param parameters - 実行対象 Promise と timeout ミリ秒。
 * @returns 実行対象 Promise の結果。
 */
const promiseWithTimeout = async <T>({
  promise,
  timeout,
}: PromiseWithTimeoutParameters<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Timeout'))
        }, timeout)
      }),
    ])
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}

export const createClient = ({
  chain,
  httpRpcs,
}: CreateClientParameters): Client => {
  const endpoints = httpRpcs.map((httpRpc) => httpRpc.url)
  const semaphore = new Semaphore(endpoints)
  const getLogsIsUsableEndpoints = new Set<string>()
  const endpointGetLogsMaxBlockRanges = new Map<string, bigint>()
  for (const httpRpc of httpRpcs) {
    if (httpRpc.getLogsIsUsable) {
      getLogsIsUsableEndpoints.add(httpRpc.url)
    }
    endpointGetLogsMaxBlockRanges.set(httpRpc.url, httpRpc.getLogsMaxBlockRange)
  }
  const busyEndpoints = new Set<string>()
  const cooldownEndpoints = new Map<string, number>()

  const getAvailableEndpoint = async (
    timeout: number,
    getLogsIsUsable = false,
  ): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const start = Date.now()
      const poll = () => {
        const now = Date.now()
        const endpoint = endpoints.find(
          (endpoint) =>
            !busyEndpoints.has(endpoint) &&
            (!cooldownEndpoints.has(endpoint) ||
              (cooldownEndpoints.get(endpoint) ?? 0) <= now) &&
            (getLogsIsUsable !== true ||
              getLogsIsUsableEndpoints.has(endpoint)),
        )
        if (endpoint !== undefined) {
          busyEndpoints.add(endpoint)
          resolve(endpoint)
          return
        }
        if (now - start > timeout) {
          reject(new Error('Timeout'))
          return
        }
        setTimeout(poll, 100)
      }
      poll()
    })
  }

  const cooldownEndpoint = (endpoint: string, time = 60_000): void => {
    cooldownEndpoints.set(endpoint, Date.now() + time)
  }

  const call = async <T>({
    clientCall,
    callName,
    timeout = 60_000,
  }: {
    clientCall: (client: PublicClient) => Promise<T>
    callName?: string
    timeout?: number
  }): Promise<T> => {
    const deadline = Date.now() + timeout
    const requestErrorEndpointIndexesInCurrentRound = new Set<number>()
    let notFoundRetryState: NotFoundRetryState | undefined

    while (true) {
      const remainingTimeout = deadline - Date.now()
      if (remainingTimeout <= 0) {
        throw new Error('Timeout')
      }

      const lease = await semaphore.acquire(
        remainingTimeout,
        notFoundRetryState?.excludedEndpointIndexes,
      )
      console.debug(`Call ${callName ?? ''} on ${lease.endpoint}`)
      try {
        const requestDeadline = Math.min(deadline, Date.now() + 15_000)
        const client = createPublicClient({
          chain,
          transport: http(lease.endpoint, {
            onFetchResponse: (response) =>
              validateRpcResponse({ response, url: lease.endpoint }),
            retryCount: 0,
            // キュー待機を含め、外側の送信期限まで transport を有効にする。
            timeout: Math.max(1, requestDeadline - Date.now()),
            onFetchRequest: async () => {
              await waitForRequest({
                key: `rpc:${new URL(lease.endpoint).origin}`,
                intervalMs: 500,
                deadline: requestDeadline,
              })
            },
          }),
        })
        const result = await promiseWithTimeout({
          promise: clientCall(client),
          timeout: Math.max(1, requestDeadline - Date.now()),
        })
        semaphore.release(lease)
        return result
      } catch (error) {
        if (isRequestError(error)) {
          if (endpoints.length === 1) {
            semaphore.release(lease)
            throw error
          }
          console.debug(error)
          if (notFoundRetryState === undefined) {
            requestErrorEndpointIndexesInCurrentRound.add(lease.index)
            if (
              requestErrorEndpointIndexesInCurrentRound.size ===
              endpoints.length
            ) {
              requestErrorEndpointIndexesInCurrentRound.clear()
            }
            semaphore.release(lease, 60_000)
            continue
          }

          notFoundRetryState.excludedEndpointIndexes.add(lease.index)
          semaphore.release(lease, 60_000)
          if (
            notFoundRetryState.excludedEndpointIndexes.size === endpoints.length
          ) {
            throw notFoundRetryState.error
          }
          continue
        }

        if (
          error instanceof TransactionNotFoundError ||
          error instanceof TransactionReceiptNotFoundError
        ) {
          console.debug(error)
          const retryState = notFoundRetryState ?? {
            error,
            excludedEndpointIndexes: new Set(
              requestErrorEndpointIndexesInCurrentRound,
            ),
          }
          notFoundRetryState = retryState
          retryState.excludedEndpointIndexes.add(lease.index)
          semaphore.release(lease)
          if (retryState.excludedEndpointIndexes.size === endpoints.length) {
            throw retryState.error
          }
          continue
        }

        semaphore.release(lease)
        throw error
      }
    }
  }

  const getEventLogs = async <T extends AbiEvent>({
    contractAddress,
    event,
    fromBlock,
    toBlock,
    timeout = 60_000,
  }: {
    readonly contractAddress: LowerHex
    readonly event: T
    readonly fromBlock: bigint
    readonly toBlock: bigint
    readonly timeout?: number
  }) => {
    const start = Date.now()

    const allLogs: GetLogsReturnType<T>[number][] = []
    let currentFromBlock = fromBlock
    let currentToBlock = 0n
    while (currentFromBlock <= toBlock) {
      const remain = timeout - (Date.now() - start)
      const endpoint = await getAvailableEndpoint(remain, true)
      busyEndpoints.add(endpoint)
      console.debug('Call getLogs on', endpoint)
      const getLogsMaxBlockRange =
        endpointGetLogsMaxBlockRanges.get(endpoint) ?? 1000n
      try {
        const requestDeadline = Math.min(start + timeout, Date.now() + 15_000)
        const client = createPublicClient({
          chain,
          transport: http(endpoint, {
            onFetchResponse: (response) =>
              validateRpcResponse({ response, url: endpoint }),
            retryCount: 0,
            // キュー待機を含め、外側の送信期限まで transport を有効にする。
            timeout: Math.max(1, requestDeadline - Date.now()),
            onFetchRequest: async () => {
              await waitForRequest({
                key: `rpc:${new URL(endpoint).origin}`,
                intervalMs: 500,
                deadline: requestDeadline,
              })
            },
          }),
        })
        currentToBlock = bigIntMin(
          currentFromBlock + getLogsMaxBlockRange - 1n,
          toBlock,
        )
        const logs = await promiseWithTimeout({
          promise: client.getLogs({
            address: contractAddress,
            event,
            fromBlock: currentFromBlock,
            toBlock: currentToBlock,
          }),
          timeout: Math.max(1, requestDeadline - Date.now()),
        })
        allLogs.push(...logs)
        currentFromBlock = currentToBlock + 1n
      } catch (error) {
        if (isRequestError(error)) {
          if (endpoints.length === 1) {
            throw error
          }
          console.debug(error)
          cooldownEndpoint(endpoint)
          continue
        }

        if (
          error instanceof TransactionNotFoundError ||
          error instanceof TransactionReceiptNotFoundError
        ) {
          console.debug(error)
          cooldownEndpoint(endpoint, 5000)
          continue
        }

        throw error
      } finally {
        busyEndpoints.delete(endpoint)
      }
    }
    return allLogs.toSorted((a, b) => Number(a.blockNumber - b.blockNumber))
  }

  const multicall = async <T extends readonly unknown[]>({
    args,
    timeout = 60_000,
  }: {
    args: MulticallParameters<T>
    timeout?: number
  }): Promise<MulticallReturnType<T>> => {
    const start = Date.now()

    if (chain.contracts?.multicall3?.address === undefined) {
      // multicall を使用しないで並列実行
      if (args.contracts.length === 0) {
        return [] as unknown as MulticallReturnType<T>
      }
      const results = await Promise.all(
        args.contracts.map((contract) =>
          (async () => {
            try {
              const result = await call({
                clientCall: (client) =>
                  client.readContract(contract as ReadContractParameters),
                timeout,
              })
              return {
                result,
                status: 'success',
              }
            } catch (error) {
              if (
                error instanceof Error &&
                error.message === 'Timeout while waiting for semaphore'
              ) {
                throw error
              }
              return {
                error,
                status: 'failure',
              }
            }
          })(),
        ),
      )
      return results as MulticallReturnType<T>
    }

    const lease = await semaphore.acquire(timeout)
    try {
      const requestDeadline = Math.min(start + timeout, Date.now() + 15_000)
      const client = createPublicClient({
        chain,
        transport: http(lease.endpoint, {
          retryCount: 0,
          // キュー待機を含め、外側の送信期限まで transport を有効にする。
          timeout: Math.max(1, requestDeadline - Date.now()),
          onFetchRequest: async () => {
            await waitForRequest({
              key: `rpc:${new URL(lease.endpoint).origin}`,
              intervalMs: 500,
              deadline: requestDeadline,
            })
          },
        }),
      })
      const results = await promiseWithTimeout({
        promise: client.multicall(args),
        timeout: Math.max(1, requestDeadline - Date.now()),
      })
      if (results.length === 0) {
        semaphore.release(lease)
        return results
      }
      const result0 = results[0]
      if (result0 === undefined || result0.status === 'success') {
        semaphore.release(lease)
        return results
      }
      const { error } = result0
      if (isRequestError(error)) {
        throw error
      }
      semaphore.release(lease)
      return results
    } catch (error) {
      if (isRequestError(error)) {
        if (endpoints.length === 1) {
          semaphore.release(lease)
          throw error
        }
        console.debug(error)
        semaphore.release(lease, 60_000)
        return await multicall({
          args,
          timeout: timeout - (Date.now() - start),
        })
      }
      semaphore.release(lease)
      throw error
    }
  }

  return {
    httpRpcCount: httpRpcs.length,
    call,
    getEventLogs,
    multicall,
  }
}

const requestErrorMessages: readonly string[] = [
  '503 Service Temporarily Unavailable',
  'Cannot fulfill request',
  'Connection interrupted',
  'Failed to fetch',
  'Gateway Timeout',
  'Header not found',
  'Internal error',
  'Limit exceeded',
  'Load balancer',
  'Missing trie node',
  'No response received',
  'Please try again',
  'Project ID request rate exceeded',
  'Rate limit',
  'Request failed',
  'Request timeout',
  'Requested resource not available',
  'Service Unavailable',
  'Service temporarily unavailable',
  'Temporary error',
  'The request took too long to respond.',
  'Too Many Requests',
  'Transaction indexing is in progress',
  'connection reset',
  'could not coalesce error',
  'execution aborted',
  'header not found',
  'limit exceeded',
  'missing trie node',
  'network changed',
  'request failed',
  'server error',
  'temporarily unavailable',
  'timeout',
  'transaction indexing is in progress',
  'getReceipt error',
  'HTTP request failed',
  'Requested resource not found',
  'Status: 404',
  'Status: 429',
  'Status: 500',
  'Status: 502',
  'Status: 521',
  'Status: 503',
  'Status: 504',
  'The request timed out.',
  'Timeout',
  'Too many requests',
  'Unauthorized',
  'Unparsable response type',
  'Web server is down',
]

/**
 * InvalidParams / MethodNotFound として返るが、endpoint 固有の制限のため他 endpoint で再試行すべきエラーメッセージ。
 */
const endpointSpecificErrorMessages: readonly string[] = [
  // publicnode: archive 要求は personal token が必要 (-32602)
  'Archive requests require a personal token',
  // viem の MethodNotFoundRpcError の message。drpc などで endpoint 側の都合により断続的に返る
  'does not exist / is not available',
]

/**
 * 他 endpoint で再試行すべき request error かどうかを判定する。
 * @param error 判定対象のエラー
 * @returns 再試行すべき場合 true
 */
export const isRequestError = (error: unknown): boolean => {
  if (
    findError(
      error,
      (cause) =>
        cause instanceof Error &&
        endpointSpecificErrorMessages.some((message) =>
          cause.message.includes(message),
        ),
    ) !== undefined
  ) {
    return true
  }

  if (
    findError(error, (cause) => cause instanceof InvalidParamsRpcError) !==
      undefined ||
    findError(error, (cause) => cause instanceof InvalidRequestRpcError) !==
      undefined ||
    findError(error, (cause) => cause instanceof MethodNotFoundRpcError) !==
      undefined
  ) {
    return false
  }

  if (
    findError(
      error,
      (cause) =>
        cause instanceof HttpRequestError ||
        cause instanceof TimeoutError ||
        cause instanceof ResourceNotFoundRpcError ||
        cause instanceof ResourceUnavailableRpcError ||
        cause instanceof JsonRpcVersionUnsupportedError ||
        cause instanceof InternalRpcError ||
        cause instanceof LimitExceededRpcError ||
        cause instanceof ParseRpcError,
    ) !== undefined
  ) {
    return true
  }

  if (
    findError(error, (cause) => {
      if (cause instanceof RpcRequestError) {
        return [-32603, -32001, -32002, -32005].includes(cause.code)
      }
      return false
    }) !== undefined
  ) {
    return true
  }

  const messageError = findError(
    error,
    (cause) =>
      cause instanceof ContractFunctionExecutionError ||
      cause instanceof RpcRequestError ||
      cause instanceof InvalidInputRpcError ||
      cause instanceof Error,
  )
  if (messageError instanceof Error) {
    return includesRequestErrorMessage(messageError.message)
  }

  return false
}

const includesRequestErrorMessage = (message: string): boolean => {
  for (const requestErrorMessage of requestErrorMessages) {
    if (message.includes(requestErrorMessage)) {
      return true
    }
  }
  return false
}

const findError = (
  error: unknown,
  predicate: (error: unknown) => boolean,
): unknown => {
  if (predicate(error)) {
    return error
  }

  if (error instanceof BaseError) {
    return error.walk(predicate) ?? undefined
  }

  if (
    error !== null &&
    typeof error === 'object' &&
    'cause' in error &&
    error.cause !== undefined
  ) {
    return findError(error.cause, predicate)
  }

  return undefined
}
